import { Behavior } from './Behavior';
import { ContentType, LessonMode, QuestionFeedbackState, isFullCreditScore, questionFeedbackFromScores } from '../../Constants';
import UtilsService from '../services/UtilsService';
import questionFeedbackManager from '../managers/QuestionFeedbackManager';

export class DependentMultiChoiceBehavior extends Behavior {
  checkForValidResponse = (responseAnswer, model) => {
    if (!responseAnswer?.answers || !Array.isArray(responseAnswer.answers)) {
      return false;
    }
    const questions = model?.questions;
    if (questions && responseAnswer.answers.length !== questions.length) {
      return false;
    }
    return responseAnswer.answers.every((entry) => {
      return entry && entry.id != null && entry.id !== '';
    });
  }

  setCorrectAnswer = (state, model) => {
    const answerSets = model.validation?.answerSets;
    if (!answerSets?.length || !model.questions) {
      return;
    }
    const correctSet = answerSets[0];
    const answers = model.questions.map((_, qIndex) => ({
      id: correctSet[qIndex] ?? null,
      isEliminated: false
    }));
    state.setCurrentResponse(UtilsService.safeMobxClone({
      answers,
      isScoredCorrect: false,
      isScored: false,
      lessonElementId: model.lessonElementId
    }));
  }

  resetStudentAnswer = (state) => {
    state.setCurrentResponse(UtilsService.safeMobxClone(state.cachedResponseAnswer));
  }

  setResponseData = (data, responseAnswer, model) => {
    const { action, questionIndex, id } = data;
    const existing = responseAnswer.answers || [];
    const length = Math.max(existing.length, questionIndex + 1);
    const newAnswers = Array.from({ length }, (_, i) => {
      const prev = existing[i];
      return prev ? { ...prev } : { id: null, isEliminated: false };
    });
    const entry = newAnswers[questionIndex];
    if (action === 'select') {
      entry.id = id;
      entry.isEliminated = false;
    } else if (action === 'eliminate') {
      if (entry.id === id) {
        entry.isEliminated = !entry.isEliminated;
      }
    }
    responseAnswer.answers = newAnswers;
  }

  getInitializedResponse = (lessonElementId, model) => {
    return {
      answers: [],
      isScoredCorrect: false,
      isScored: false,
      lessonElementId
    };
  }

  isAutoScored = (model) => {
    return (ContentType.getAutoscore(model.type) &&
      !model.survey && !model.unscored);
  }

  getCorrectAnswersText = (model) => {
    const answerSets = model?.validation?.answerSets;
    const questions = model?.questions;

    if (!Array.isArray(answerSets) || answerSets.length === 0 || !Array.isArray(questions) || questions.length === 0) {
      return '<div></div>';
    }

    const optionNames = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    let text = '';

    answerSets.forEach((set, setIndex) => {
      if (!Array.isArray(set)) {
        return;
      }

      text += "<div class='answer-list-row'>";
      text += `<div class='list-number'>${setIndex + 1}</div>`;
      text += "<div class='correct-icon'></div>";
      text += "<div class='answer-text'>";

      set.forEach((answerId, questionIndex) => {
        if (!answerId) {
          return;
        }

        const question = questions[questionIndex];
        const answers = question?.answers || [];
        const answerIdx = answers.findIndex((a) => a.id === answerId);
        if (answerIdx === -1) {
          return;
        }

        const letter = optionNames[answerIdx] || '';
        const answerText = answers[answerIdx].text || '';

        text += "<div class='dependent-multichoice-accepted-answer-row'>";
        text += `<span class='answer-label'>${questionIndex + 1}) </span>`;
        text += `<span class='radio-symbol  disabled'>${letter} </span>`;
        text += `<span class='answer-value'>${answerText}</span>`;
        text += "</div>";
      });

      text += "</div>"; // .answer-text
      text += "</div>"; // .answer-list-row
    });

    return text || '<div></div>';
  }

  getQuestionFeedbackText = (model, correctFeedback, incorrectFeedback, correctFeedbackBody, incorrectFeedbackBody, mode, questionIsCorrect, currentAttemptCount, { lessonElementState }) => {
    let textBody = '';
    if (mode === LessonMode.PREVIEW || mode === LessonMode.ITEM_PREVIEW || mode === LessonMode.STUDENT_PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW) {
      textBody += UtilsService.previewWarning();
    } else {
      if (typeof currentAttemptCount !== 'undefined' && currentAttemptCount !== -1 && currentAttemptCount > 1) {
        textBody += UtilsService.saveWarning();
      } else {
        textBody += UtilsService.saveCommit();
      }
    }

    let textHeader = '';
    let attemptsText = '';

    if (questionFeedbackManager.isPractice &&
        (questionIsCorrect === QuestionFeedbackState.INCORRECT || questionIsCorrect === QuestionFeedbackState.PARTIAL_CORRECT) &&
        model.lessonElementId) {
      attemptsText = questionFeedbackManager.getAttemptsRemainingText(model.lessonElementId);
    }

    if (questionIsCorrect === QuestionFeedbackState.CORRECT) {
      textHeader = correctFeedback;
      if (correctFeedbackBody) {
        textBody = `${correctFeedbackBody} ${textBody}`;
      }
    } else if (questionIsCorrect === QuestionFeedbackState.PARTIAL_CORRECT) {
      textHeader = `${UtilsService.partiallyCorrect()}`;
      const custom = incorrectFeedbackBody ? `${incorrectFeedbackBody } ` : '';
      textBody = `${UtilsService.partiallyCorrectPrompt()}. <br/>${custom}${textBody}`;
    } else {
      textHeader = incorrectFeedback;
      if (incorrectFeedbackBody) {
        textBody = `${incorrectFeedbackBody} ${textBody}`;
      }
    }

    if (attemptsText) {
      textHeader += attemptsText;
    }

    let finalText = '<div class=\'question-feedback-header\'>';
    finalText += textHeader;
    finalText += '</div>';
    finalText += '<div class=\'question-feedback-body\'>';
    finalText += textBody;
    finalText += '</div>';

    return finalText;
  }

  isAnswerCorrect = (_answerId, lessonElementState, _model) => {
    if (lessonElementState.correctAnswerShowing) {
      return true;
    }
    return lessonElementState.isScored && isFullCreditScore(lessonElementState.submittedItemScore, lessonElementState.maxScore);
  }

  isQuestionCorrect = (lessonElementState, lessonElementId) => {
    if (!lessonElementState) {
      return QuestionFeedbackState.INCORRECT;
    }
    return questionFeedbackFromScores(lessonElementState.submittedItemScore, lessonElementState.maxScore);
  }
}

export default new DependentMultiChoiceBehavior();
